import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, trackPageview } from "../lib/api";
import { CATEGORY_COLORS, ISLAND_BY_SLUG } from "../lib/constants";
import { LangContext } from "../lib/LangContext";
import { t as translate, tCategory as translateCat, timeAgo as fmtTime, useReactiveLang} from "../lib/i18n";
import { KeyFacts } from "../components/KeyFacts";
import { SentimentDot } from "../components/SentimentDot";
import { ShareButtons } from "../components/ShareButtons";
import { ArticleCTALine } from "../components/ArticleCTALine";
import { SharedReferrerBanner } from "../components/SharedReferrerBanner";
import { ReadingStreak } from "../components/ReadingStreak";
import { recordArticleRead } from "../lib/readerHistory";
import { GoogleTranslateLink } from "../components/GoogleTranslateLink";
import { LeaderboardAd, RectangleAd } from "../components/AdSlot";
import { ArticleCard } from "../components/ArticleCard";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";

const LANG_KEY = "ir_lang";

const readingMinutes = (summary) => {
  const words = (summary || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

export default function ArticlePage() {
  // __TOGGLE_HOOK__: self-contained reactive lang state for the mini-header toggle.
  const [__toggleLang, __setToggleLang] = useReactiveLang();

  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [ads, setAds] = useState({});
  const [imgFailed, setImgFailed] = useState(false);
  const [lang] = useState(() => {
    try {
      const stored = localStorage.getItem(LANG_KEY);
      if (stored && ["en", "es", "nl"].includes(stored)) return stored;
      // Same browser-locale detection as HomePage so deep-links land in
      // the visitor's language even before they interact with the toggle.
      const nav = (navigator.language || navigator.userLanguage || "").toLowerCase();
      if (nav.startsWith("nl")) return "nl";
      if (nav.startsWith("es")) return "es";
    } catch (e) { /* ignore */ }
    return "en";
  });
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    setImgFailed(false);
    window.scrollTo({ top: 0, behavior: "instant" });
    api
      .get(`/articles/${id}`, { params: { lang } })
      .then((r) => {
        setArticle(r.data);
        trackPageview(`/article/${id}`, id);
        recordArticleRead(id);
      })
      .catch(() => setNotFound(true));
  }, [id, lang]);

  useEffect(() => {
    if (!article) return;
    document.title = `${article.title} · IslandsDaily`;
    // Update OG meta tags for share previews
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [tag, key] = selector
          .replace(/[[\]'"]/g, "")
          .split("=");
        const name = tag.startsWith("meta") ? tag.slice(4) : tag;
        el.setAttribute(name, key);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    const summary = article.ai_summary || "";
    const first2 = summary.split(/(?<=[.!?])\s+/).slice(0, 2).join(" ");
    setMeta('meta[name="description"]', "content", first2);
    setMeta('meta[property="og:title"]', "content", article.title);
    setMeta('meta[property="og:description"]', "content", first2);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:site_name"]', "content", "IslandsDaily");
    setMeta('meta[property="og:locale"]', "content", "en_US");
    setMeta('meta[property="og:updated_time"]', "content", article.published_at || "");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:site"]', "content", "@IslandsDaily");
    setMeta('meta[name="twitter:title"]', "content", article.title);
    setMeta('meta[name="twitter:description"]', "content", first2);
    if (article.image_url) {
      setMeta('meta[property="og:image"]', "content", article.image_url);
      setMeta('meta[name="twitter:image"]', "content", article.image_url);
    }
    // Canonical link
    const canonicalHref = window.location.href.split("#")[0];
    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalHref);

    // NewsArticle JSON-LD
    const ld = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "headline": article.title,
      "description": first2,
      "image": article.image_url || undefined,
      "datePublished": article.published_at,
      "dateModified": article.published_at,
      "author": { "@type": "Organization", "name": "IslandsDaily" },
      "publisher": {
        "@type": "Organization",
        "name": "IslandsDaily",
        "logo": {
          "@type": "ImageObject",
          "url": `${window.location.origin}/logo.png`,
        },
      },
      "mainEntityOfPage": canonicalHref,
      "keywords": `${article.island},${article.category},Caribbean news,Dutch Caribbean`,
      "inLanguage": "en",
      "isAccessibleForFree": true,
    };
    let ldEl = document.getElementById("ld-newsarticle");
    if (!ldEl) {
      ldEl = document.createElement("script");
      ldEl.id = "ld-newsarticle";
      ldEl.type = "application/ld+json";
      document.head.appendChild(ldEl);
    }
    ldEl.textContent = JSON.stringify(ld);

    return () => {
      const el = document.getElementById("ld-newsarticle");
      if (el) el.remove();
    };
  }, [article]);

  useEffect(() => {
    if (!article) return;
    api
      .get("/articles", {
        params: {
          island: article.island,
          category: article.category,
          limit: 6,
          lang,
        },
      })
      .then((r) => {
        const list = (r.data || []).filter((a) => a.id !== article.id).slice(0, 3);
        setRelated(list);
      })
      .catch(() => setRelated([]));
    api
      .get("/ads", { params: { island: article.island } })
      .then((r) => setAds(r.data || {}))
      .catch(() => {});
  }, [article, lang]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-serif-display text-3xl text-[#1A2E35] mb-2">
          Article not found
        </h1>
        <p className="text-sm text-[#6B7280] mb-6">
          This story may have been removed or the link is incorrect.
        </p>
        <Link
          to="/"
          className="px-5 py-2 rounded-full bg-[#0AADA0] hover:bg-[#08988c] text-white text-sm transition-colors"
          data-testid="article-not-found-home-btn"
        >
          Back to IslandsDaily
        </Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-[#6B7280] text-sm">
        Loading...
      </div>
    );
  }

  const color = CATEGORY_COLORS[article.category] || "#0AADA0";
  const island = ISLAND_BY_SLUG[article.island];
  const minutes = readingMinutes(article.ai_summary);
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  // Social-share URL points at the backend OG-preview endpoint. When pasted
  // into WhatsApp/iMessage/Slack the crawler reads the article-specific OG
  // tags there and shows a rich preview. The endpoint meta-refreshes humans
  // straight to `pageUrl` so UX is unchanged.
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const shareUrl = article.id
    ? `${apiBase}/api/og/article/${article.id}`
    : pageUrl;

  return (
    <LangContext.Provider value={lang}>
      <div className="min-h-screen bg-white" data-testid="article-page">
        {/* Slim back bar */}
        <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
              className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7280] hover:text-[#1A2E35] transition-colors"
              data-testid="article-back-btn"
            >
              <ArrowLeft size={15} /> Back
            </button>
            <Link
              to="/"
              className="ml-auto font-serif-display text-[18px] leading-none"
              data-testid="article-home-link"
            >
              <span className="text-[#1A1A1A]">Islands</span>
              <span className="text-[#0AADA0]">Daily</span>
            </Link>
            {/* __LANG_TOGGLE__ */}
            <LanguageToggle lang={__toggleLang} onChange={__setToggleLang} />
          </div>
        </div>

        <SharedReferrerBanner islandSlug={article.island || "all"} />

        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          {/* Hero image */}
          <div
            className="w-full overflow-hidden rounded-[12px] mb-6 bg-[#F5F5F5]"
            style={{ maxHeight: 400 }}
          >
            {article.image_url && !imgFailed ? (
              <div className="relative">
                <img
                  src={article.image_url}
                  alt={article.title}
                  onError={() => setImgFailed(true)}
                  className="w-full object-cover block"
                  style={{ maxHeight: 400 }}
                  data-testid="article-hero-img"
                  loading="eager"
                />
                <span
                  className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/45 text-white text-[10px]"
                  data-testid="article-hero-credit"
                >
                  Photo: {article.source_name}
                </span>
              </div>
            ) : (
              <div
                className="w-full flex items-center justify-center relative overflow-hidden"
                style={{
                  height: 280,
                  background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
                }}
                data-testid="article-hero-fallback"
              >
                <div
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(45deg, rgba(255,255,255,.2) 0 2px, transparent 2px 22px)",
                  }}
                />
                <span className="font-serif-display italic text-white text-4xl relative">
                  {article.category}
                </span>
              </div>
            )}
          </div>

          {/* Badge row */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span
              className="inline-block px-2.5 py-0.5 rounded-full text-[11px] uppercase tracking-wider text-white"
              style={{ background: color }}
              data-testid="article-category-badge"
            >
              {article.category}
            </span>
            {island && (
              <span
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] uppercase tracking-wider bg-[#F5F5F5] text-[#6B7C82]"
                data-testid="article-island-badge"
              >
                <span aria-hidden="true">{island.flag}</span>
                {island.name}
              </span>
            )}
          </div>

          {/* Headline */}
          <h1
            className="font-serif-display text-[28px] sm:text-[34px] leading-[1.2] text-[#1A2E35] mb-3"
            data-testid="article-headline"
          >
            {article.title}
          </h1>

          {/* Byline */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-[#6B7280] mb-5">
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0AADA0] hover:underline font-medium"
              data-testid="article-source-link"
              title={translate(lang, "article_source_link_title")}
            >
              Source: {article.source_name}
            </a>
            <span className="text-[#9CA3AF]">·</span>
            <span>{fmtTime(article.published_at, lang)}</span>
            <span className="text-[#9CA3AF]">·</span>
            <span className="inline-flex items-center gap-1">
              <span aria-hidden="true">{island?.flag}</span>
              {island?.name}
            </span>
            <span className="text-[#9CA3AF]">·</span>
            <span data-testid="article-read-time">{minutes} min read</span>
            <SentimentDot sentiment={article.sentiment} testId="article-sentiment" />
          </div>

          <div className="h-px bg-[#EEEEEE] my-6" />

          {/* Summary */}
          <p
            className="text-[16px] text-[#1A2E35] mb-2"
            style={{ lineHeight: 1.8, fontFamily: "'DM Sans', system-ui, sans-serif" }}
            data-testid="article-summary"
          >
            {article.ai_summary}
          </p>

          <KeyFacts facts={article.key_facts} testId="article-key-facts" />

          {/* Share */}
          <div className="mt-7">
            <ShareButtons headline={article.title} pageUrl={pageUrl} shareUrl={shareUrl} />
          </div>

          <ArticleCTALine />

          <div className="h-px bg-[#EEEEEE] my-7" />

          {/* Original-source CTA */}
          <div className="mb-2">
            <GoogleTranslateLink
              originalUrl={article.url}
              sourceName={article.source_name}
              size="lg"
              testIdPrefix="article-gt"
            />
          </div>
          <p
            className="text-[11px] text-[#9CA3AF] mt-1"
            data-testid="article-translate-disclaimer"
          >
            {translate(lang, "leaving_disclaimer")}
          </p>

          {/* In-content leaderboard ad */}
          {ads.leaderboard && (
            <div className="mt-10">
              <LeaderboardAd ad={ads.leaderboard} />
            </div>
          )}
        </article>

        {/* Related */}
        {related.length > 0 && (
          <section className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
            <h2
              className="text-[11px] uppercase tracking-[0.18em] text-[#0AADA0] mb-5"
              data-testid="related-heading"
            >
              {translate(lang, "more_from")} {translateCat(lang, article.category)} · {island?.name || "the Caribbean"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related[0] && (
                <ArticleCard article={related[0]} index={0} showIslandFlag={false} />
              )}
              {ads.rectangle ? (
                <div data-testid="article-page-rect-ad">
                  <RectangleAd ad={ads.rectangle} />
                </div>
              ) : (
                related[1] && (
                  <ArticleCard article={related[1]} index={1} showIslandFlag={false} />
                )
              )}
              {ads.rectangle && related[1] && (
                <ArticleCard article={related[1]} index={1} showIslandFlag={false} />
              )}
              {related[2] && (
                <ArticleCard article={related[2]} index={2} showIslandFlag={false} />
              )}
            </div>
          </section>
        )}

        <ReadingStreak />

        <Footer />
      </div>
    </LangContext.Provider>
  );
}
